import { ImpactMap } from '@/features/impacts/model';
import { createObjectCsvStringifier as createCsvStringifier } from 'csv-writer';
import { Requirement } from '@/features/sr-model/Requirement';
import { QmsRequirement } from '@/services/model';
import { SopGetters } from '@/features/sops';
import { SopSearchPart } from '@/model';
import { TemplateContentGetters } from '@/features/template-content/template-content-store';
import { TemplateContent } from '@/features/template-content/template-content-model';
import { TemplateGetters } from '@/store/templates';
import { Template } from '@/model/Template';
import { RegulationDetailGetters, RegulationDetailState } from '@/store/regulation-detail';
import { ProductRequirement } from '@/features/sop-block/model';
import { ImpactAnalysis } from '@/features/impact-analysis/model';

type CsvEntry = {
    fReqId?: string;
    fReqText?: string;
    fReqApp?: string;
    fReqRea?: string;
    fReqEvi?: string;
    fSrRef?: string;
    iCha?: string;
    iChaTxt?: string;
    iImp?: string;
    iImpTxt?: string;
}

function strip(html: string) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
}

function requirementTextWithOptionalParagraph(text: string, paragraph?: string) {
    if (!paragraph || paragraph.length == 0) {
        return text;
    }
    return paragraph + ' ' + text;
}

function exportImpact(impactId: string | undefined, impacts: ImpactMap, impactAnalysis: ImpactAnalysis) {
    if (!impactId) {
        return {};
    }
    const impact = impacts[impactId];
    return {
        iCha: impact.changeType,
        iChaTxt: impact.changeDescription,
        iImp: impact.impactType,
        iImpTxt: impact.impactDescription,
        iLink: process.env.VUE_APP_REGULATIONS_BASE_URL + '/impact-analysis/' + impactAnalysis.id + '#impact=' + impactId,
    }
}

function naIfEmpty(value: string) {
    if (!value || value === '') {
        return 'n/a'
    }
    return value;
}

function evidences(qmsRequirement: QmsRequirement, state: RegulationDetailState, getters: any, suffix: string) {

    const processEvidences = (getters[SopGetters.SELECTABLE_SOPS_BY_ARTIFACT_IDS](qmsRequirement.targetSopArtifactIds) as SopSearchPart[])
        .map(sop => sop.name)
        .join('\n');
    const processEvidencesId = (getters[SopGetters.SELECTABLE_SOPS_BY_ARTIFACT_IDS](qmsRequirement.targetSopArtifactIds) as SopSearchPart[])
        .map(sop => sop.versionId)
        .join('\n');

    const templateContentEvidences = (getters[TemplateContentGetters.TEMPLATE_CONTENTS_BY_IDS_OR_LOADING](qmsRequirement.templateContentIds) as TemplateContent[])
        .map(templateContent => templateContent.name)
        .join('\n');
    const templateContentEvidencesId = (getters[TemplateContentGetters.TEMPLATE_CONTENTS_BY_IDS_OR_LOADING](qmsRequirement.templateContentIds) as TemplateContent[])
        .map(templateContent => templateContent.id)
        .join('\n');

    const templateEvidences = (getters[TemplateGetters.VALID_TEMPLATE_ARTIFACTS] as Template[])
        .filter(template => qmsRequirement.targetTemplateArtifactIds.includes(template.artifactId))
        .map(templateContent => templateContent.name)
        .join('\n');
    const templateEvidencesId = (getters[TemplateGetters.VALID_TEMPLATE_ARTIFACTS] as Template[])
        .filter(template => qmsRequirement.targetTemplateArtifactIds.includes(template.artifactId))
        .map(templateContent => templateContent.versionId)
        .join('\n');

    const productEvidences = (getters[RegulationDetailGetters.PRODUCT_REQUIREMENTS] as ProductRequirement[])
        .filter(product => qmsRequirement.targetSopBlockArtifactIds.includes(product.sopBlockArtifactId))
        .map(productRequirement => productRequirement.sopBlockName)
        .join('\n');
    const productEvidencesId = (getters[RegulationDetailGetters.PRODUCT_REQUIREMENTS] as ProductRequirement[])
        .filter(product => qmsRequirement.targetSopBlockArtifactIds.includes(product.sopBlockArtifactId))
        .map(productRequirement => productRequirement.sopBlockVersionId)
        .join('\n');

    return ({
        [suffix + 'ReqEviProc']: naIfEmpty(processEvidences),
        [suffix + 'ReqEviTc']: naIfEmpty(templateContentEvidences),
        [suffix + 'ReqEviTemp']: naIfEmpty(templateEvidences),
        [suffix + 'ReqEviProd']: naIfEmpty(productEvidences),
        [suffix + 'ReqEviProcId']: processEvidencesId,
        [suffix + 'ReqEviTcId']: templateContentEvidencesId,
        [suffix + 'ReqEviTempId']: templateEvidencesId,
        [suffix + 'ReqEviProdId']: productEvidencesId,
    });
}

function addAmendment(requirement: Requirement) {
    if (!requirement.amendment) {
        return '';
    }
    return '\n' + requirement.amendment.key + ' ' + requirement.amendment.type + ' before ' + requirement.amendment.date + ': ' + requirement.amendment.oldText + ''
}

function exportFrom(requirements: Requirement[], state: RegulationDetailState, getters: any) {
    let currentTitle = '';
    return requirements.map(requirement => {
        const qmsRequirement = state.qmsRequirements[requirement.id].interpretedInRequirementId ? state.qmsRequirements[state.qmsRequirements[requirement.id].interpretedInRequirementId || ''] : state.qmsRequirements[requirement.id];
        currentTitle = requirement.type == 'HEADING' ? strip(requirement.text) : currentTitle
        const srRef = (getters[RegulationDetailGetters.REGULATION_REFERENCES_BY_REQUIREMENT_ID](requirement.id) as string).trim()
        return ({
            fReqId: requirement.id,
            fReqTitle: currentTitle,
            fReqText: requirementTextWithOptionalParagraph(strip(requirement.text), requirement.paragraph) + addAmendment(requirement),
            fReqInh: state.qmsRequirements[requirement.id].interpretedInRequirementId || '',
            fReqInt: requirement.interpretable,
            fReqApp: qmsRequirement.applicability,
            fReqRea: naIfEmpty(qmsRequirement.applicabilityReason),
            fReqNot: naIfEmpty(qmsRequirement.notes),
            fSrRef: naIfEmpty(strip(srRef.replaceAll('</li>', '</li>\n').replaceAll('<li>', '<li> - '))),
            fReqLink: 'https://app.colabon.com/sr-list/' + state.regulation.id + '#' + requirement.key,
            ...evidences(qmsRequirement, state, getters, 'f')
        });
    })
}

function mergeInheritedFrom<T extends { fReqId: string; fReqText: string; fReqInt: boolean; fReqInh: string; fSrRef: string }>(exportEntries: T[]): T[] {

    const returnEntries = [];
    let lastInterpretedEntry = null;
    for (const exportEntry of exportEntries) {
        if (lastInterpretedEntry && !exportEntry.fReqInt) {
            lastInterpretedEntry.fReqText = lastInterpretedEntry.fReqText + '\n' + exportEntry.fReqText;
            lastInterpretedEntry.fSrRef = lastInterpretedEntry.fSrRef + (lastInterpretedEntry.fSrRef.length > 0 ? '\n\n' : '') + exportEntry.fSrRef;
        } else {
            lastInterpretedEntry = exportEntry;
            returnEntries.push(lastInterpretedEntry);
        }
    }
    return returnEntries;
}

function download(content: string, fileName: string) {
    const a = document.createElement('a');
    const mimeType = 'text/csv;encoding:utf-8';

    if (URL && 'download' in a) { // html5 A[download]
        a.href = URL.createObjectURL(new Blob([content], {
            type: mimeType
        }));
        a.setAttribute('download', fileName);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    } else {
        location.href = 'data:application/octet-stream,' + encodeURIComponent(content); // only this mime type is supported
    }
}

export class GapListCsvExporter {

    static exportGapListAsCsv(state: RegulationDetailState, getters: any) {

        const filteredRequirements = getters[RegulationDetailGetters.FILTERED_REQUIREMENTS] as Requirement[];

        const exportEntries = exportFrom(filteredRequirements, state, getters);
        const csvEntries = mergeInheritedFrom(exportEntries);

        const csvStringifier = createCsvStringifier({
            header: [
                { id: 'fReqId', title: 'Id' },
                { id: 'fReqTitle', title: 'Title' },
                { id: 'fReqText', title: 'Text' },
                { id: 'fReqInh', title: 'Inherited' },
                { id: 'fReqApp', title: 'Applicability' },
                { id: 'fReqRea', title: 'Reason' },
                { id: 'fReqNot', title: 'Note' },
                { id: 'fSrRef', title: 'Referencing Standards' },
                { id: 'fReqLink', title: 'Requirement Link' },

                { id: 'fReqEviProc', title: 'Process Evidences' },
                { id: 'fReqEviTc', title: 'Template Content Evidences' },
                { id: 'fReqEviTemp', title: 'Template Evidences' },
                { id: 'fReqEviProd', title: 'Product Evidences' },

                { id: 'fReqEviProcId', title: 'Process Evidences ID' },
                { id: 'fReqEviTcId', title: 'Template Content Evidences ID' },
                { id: 'fReqEviTempId', title: 'Template Evidences ID' },
                { id: 'fReqEviProdId', title: 'Product Evidences ID' },

            ]
        });

        const header = csvStringifier.getHeaderString();
        const content = csvStringifier.stringifyRecords(csvEntries);

        const filename = state.regulation.id;

        download(header + '\n' + content, 'GapList_' + filename + '.csv');

        return csvEntries;
    }
}